import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { SmallClassCodeComboBoxModel } from './small-class-code-combo-box.model';
import { GetSmallClassListService } from './api-services/get-small-class-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 小分類コードコンボボックスを制御するクラス
 *
 * @export SmallClassCodeComboBoxComponent
 * @class SmallClassCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-small-class-code-combo-box',
  templateUrl: 'small-class-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SmallClassCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetSmallClassListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetSmallClassListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]

    }
  ],
})

export class SmallClassCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedOrderCode: string = <string>'';                                // 受注コード
  public selectedClassCode: string = <string>'';                                // 部門コード
  public selectedLargeClassCode: string = <string>'';                           // 大分類コード
  public selectedMiddleClassCode: string = <string>'';                          // 中分類コード
  public selectedSmallClassCode: string = <string>'';                           // 小分類コード
  public selectedSmallClassName: string = <string>'';                           // 小分類名

  public model: SmallClassCodeComboBoxModel = new SmallClassCodeComboBoxModel(); // マネージャ

  public selectedIndex: number = <number>-1;

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;

  @Input() blank: boolean = <boolean>true;
  @Input() required: boolean = <boolean>false;
  @Input() orderCode: string = <string>'';
  @Input() classCode: string = <string>'';
  @Input() largeClassCode: string = <string>'';
  @Input() middleClassCode: string = <string>'';
  @Input() disabled: boolean = <boolean>false;
  @Input() isInitOrderCode: string = <string>'';
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() classCodeComboBoxFlg: boolean = <boolean>false;
  @Input() largeClassCodeComboBoxFlg: boolean = <boolean>false;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() forcedEnabledMode: string = null;

  /**
   * Creates an instance of SmallClassCodeComboBoxComponent.
   * @param {GetSmallClassListService} getSmallClassListService 小分類情報リストを取得する
   * @memberof SmallClassCodeComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getSmallClassListService: GetSmallClassListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedOrderCode) {
      this.selectedOrderCode = text;
      this.setSmallClassCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    this.reload();
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof SmallClassCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'middleClassCode':
          this.selectedIndex = -1;
          this.middleClassCode = changes.middleClassCode.currentValue;
          if (!PlessViewUtilsSub.isEmpty(this.middleClassCode)) {
            this.getData();
          } else {
            this.clear();
          }
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * 条件パラメータの設定
   *
   * @param {string} orderCode
   * @memberof SmallClassCodeComboBoxComponent
   */
  public setConditionParams(orderCode: string): void {
    this.orderCode = orderCode;
    // プロパティ初期化フラグを設定
    this.model.isInitOrderCode = true;
    // データ取得
    this.getSmallClassListService.execute(this.model, this.orderCode, this.classCode,
      this.largeClassCode, this.middleClassCode);
  }

  /**
   * コンボボックスの選択小分類コードを切り替える
   *
   * @param {string} smallClassCode 小分類コード
   * @memberof SmallClassCodeComboBoxComponent
   */
  public setSmallClassCode(smallClassCode: string): void {
    this.model.setSmallClassCode = smallClassCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedSmallClassCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  public updateSelectedSmallClassCode(): void {
    const orderList: Array<any> = this.model.smallClassList;
    for (let i = Number(0); i < orderList.length; i++) {
      if (PlessViewUtilsSub.trim(orderList[i].SmallClassCode) === this.model.setSmallClassCode) {
        this.selectedIndex = i;
        this.selectedClassCode = this.model.smallClassList[this.selectedIndex].ClassCode;
        this.selectedLargeClassCode = this.model.smallClassList[this.selectedIndex].LargeClassCode;
        this.selectedMiddleClassCode = this.model.smallClassList[this.selectedIndex].MiddleClassCode;
        this.selectedSmallClassCode = this.model.smallClassList[this.selectedIndex].SmallClassCode;
        this.selectedSmallClassName = this.model.smallClassList[this.selectedIndex].SmallClassName;
        this.update(null);
      }
    }

    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 条件にてコンボボックス内容を再ロードする
   *
   * @param {string} [middleClassCode=''] 中分類コード
   * @returns {void}
   * @memberof SmallClassCodeComboBoxComponent
   */
  public reload(middleClassCode = ''): void {
    if ((this.classCodeComboBoxFlg && PlessViewUtilsSub.isEmpty(this.classCode)) ||
        (this.largeClassCodeComboBoxFlg && PlessViewUtilsSub.isEmpty(this.largeClassCode)) ||
      PlessViewUtilsSub.isEmpty(middleClassCode)) {
      this.clear();
      return;
    }

    // データ取得
    this.getSmallClassListService.execute(this.model, this.orderCode,
      this.classCode, this.largeClassCode, this.middleClassCode);

    this.selectedClassCode = '';
    this.selectedLargeClassCode = '';
    this.selectedMiddleClassCode = '';
    this.selectedSmallClassCode = '';
    this.selectedSmallClassName = '';
  }

  /**
   * 更新
   *
   * @param {*} e
   * @returns {void}
   * @memberof SmallClassCodeComboBoxComponent
   */
  public update(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedClassCode = '';
      this.selectedLargeClassCode = '';
      this.selectedMiddleClassCode = '';
      this.selectedSmallClassCode = '';
      this.selectedSmallClassName = '';
      return;
    }

    this.selectedClassCode = this.model.smallClassList[this.selectedIndex].ClassCode;
    this.selectedLargeClassCode = this.model.smallClassList[this.selectedIndex].LargeClassCode;
    this.selectedMiddleClassCode = this.model.smallClassList[this.selectedIndex].MiddleClassCode;
    this.selectedSmallClassCode = this.model.smallClassList[this.selectedIndex].SmallClassCode;
    this.selectedSmallClassName = this.model.smallClassList[this.selectedIndex].SmallClassName;
  }

  /**
   * 初期化処理
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  public clear(): void {
    this.model = new SmallClassCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;

    this.selectedOrderCode = <string>'';
    this.selectedClassCode = <string>'';
    this.selectedLargeClassCode = <string>'';
    this.selectedMiddleClassCode = <string>'';
    this.selectedSmallClassCode = <string>'';
    this.selectedSmallClassName = <string>'';
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof SmallClassCodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.setSelectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.setSelectedIndex >= 0) {
      this.selectedIndex = this.model.setSelectedIndex;
      this.model.setSelectedIndex = -1;
    }

    this.update(null);
  }

  /**
   * データ取得
   *
   * @memberof SmallClassCodeComboBoxComponent
   */
  public getData(): void {
    this.getSmallClassListService.execute(this.model,
      this.orderCode, this.classCode, this.largeClassCode, this.middleClassCode);
  }

}
